import axios from 'axios';
import { waitForClerk } from '../utils/clerkReady';
import i18n from '../i18n';
import { showToast } from './toastService';

// Legge l'URL base dell'API dal file .env
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  // Fail fast instead of hanging on the browser's TCP timeout (1-2 min) when the
  // connection drops; also keeps the 30s token poll from stacking in-flight requests.
  timeout: 20000,
  // Non usiamo più cookie per l'autenticazione
});

// Request interceptor: aggiungi Clerk session token.
// Esportata perché anche il dialog GPT-Live la riusa via questo stesso client.
export async function attachAuth(config) {
  try {
    // Usa window.Clerk invece di useAuth() perché siamo fuori dal contesto Vue.
    // Attendi che clerk-js sia davvero caricato: Clerk.loaded è un booleano,
    // NON una promise — `await window.Clerk.loaded` non aspettava nulla e le
    // richieste in gara col load partivano senza Authorization (→ 401).
    await waitForClerk();
    if (window.Clerk) {
      // Ottieni token dalla sessione corrente
      const token = await window.Clerk.session?.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (error) {
    Console.error('[API] Error getting Clerk token:', error);
  }

  return config;
}

api.interceptors.request.use(attachAuth, (error) => {
  return Promise.reject(error);
});

// Flag per evitare loop di redirect
let isRedirecting = false;

// Accesso a un workspace perso a metà sessione (membro rimosso o workspace eliminato
// dall'owner): codice di checkWorkspaceAccess → status atteso e chiave i18n del toast.
// `methods` (opzionale, assente = qualsiasi) limita i metodi HTTP considerati.
const WORKSPACE_LOSS_ERRORS = {
  WORKSPACE_ACCESS_DENIED: { status: 403, toastKey: 'workspace.accessDenied' },
  // Solo GET: checkTokenAllowance (POST/PUT di flow e QA) risponde 404 WORKSPACE_NOT_FOUND
  // con lo stesso body quando l'utente non possiede un workspace, anche se ha accesso a
  // quello nell'URL. Un workspace eliminato emerge comunque dalle GET della pagina.
  WORKSPACE_NOT_FOUND: { status: 404, toastKey: 'workspace.notFound', methods: ['get'] },
};

// ObjectId del workspace nell'URL della richiesta (/workspaces/:id o /workspaces/:id/...)
const WORKSPACE_ID_IN_URL = /\/workspaces\/([0-9a-f]{24})(?:[/?#]|$)/i;

// Flag per mostrare un solo toast e fare una sola navigazione quando N richieste
// parallele falliscono insieme per lo stesso workspace
let isLeavingWorkspace = false;

// Gestisce 403 WORKSPACE_ACCESS_DENIED / 404 WORKSPACE_NOT_FOUND: rimuove il workspace
// dallo store e, se è quello della route corrente, avvisa l'utente e lo porta fuori.
// - forgetWorkspace() va chiamato PRIMA di navigare: altrimenti la guard di /app/bots
//   risolve lastWorkspaceId dal localStorage e rimanda l'utente nel workspace negato,
//   ripetendo il 403 in loop.
// - Router e store sono importati dinamicamente perché entrambi importano api.js: un
//   import statico creerebbe un ciclo (stesso pattern di resolveWorkspaceId nel router).
// - Navigazione SPA (router.push), non window.location, così il toast sopravvive.
// Fire-and-forget: non lancia mai, gli errori finiscono solo nel log.
async function handleWorkspaceLoss(error) {
  const lossError = WORKSPACE_LOSS_ERRORS[error.response?.data?.error?.code];
  if (!lossError || error.response?.status !== lossError.status) return;
  const method = (error.config?.method || '').toLowerCase();
  if (lossError.methods && !lossError.methods.includes(method)) return;

  const deniedId = error.config?.url?.match(WORKSPACE_ID_IN_URL)?.[1];
  if (!deniedId) return;

  try {
    const [{ default: router }, { useWorkspaceStore }] = await Promise.all([
      import('../router'),
      import('../stores/workspaceStore'),
    ]);

    useWorkspaceStore().forgetWorkspace(deniedId);

    // Workspace diverso da quello aperto (o uscita già in corso): basta lo store
    if (router.currentRoute.value.params.workspaceId !== deniedId || isLeavingWorkspace) return;

    isLeavingWorkspace = true;
    showToast(i18n.global.t(lossError.toastKey), 'warn');
    await router.push('/app/bots').finally(() => {
      isLeavingWorkspace = false;
    });
  } catch (err) {
    Console.error('[API] Error handling lost workspace access:', err);
  }
}

// Response interceptor: gestisci errori
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Un 401 sul token della conversazione (chat pubblica) non è una sessione scaduta:
    // il chiamante lo gestisce, l'editor non deve finire sul login (card #10434653609).
    const isConversationTokenError = error.response?.data?.error?.code === 'CONVERSATION_TOKEN_INVALID';
    if (error.response?.status === 401 && !isConversationTokenError && !isRedirecting) {
      // Evita loop infinito
      isRedirecting = true;

      Console.warn('[API] 401 received - redirecting to sign-in');

      // Redirect alla pagina di sign-in interna (SPA, HTML5 route)
      window.location.href = '/sign-in';
    }

    // Fire-and-forget: il reject verso il chiamante non viene ritardato né alterato
    handleWorkspaceLoss(error);

    return Promise.reject(error);
  }
);

export default api;
